import { useState, useCallback, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { OtpInput } from '../components/auth/OtpInput'
import { CountdownTimer } from '../components/auth/CountdownTimer'
import { useTranslation } from 'react-i18next'

type Step = 'identifier' | 'otp'

export function LoginPage() {
  const { t } = useTranslation()
  const { requestOtp, verifyOtp } = useAuth()
  const navigate = useNavigate()

  const [searchParams] = useSearchParams()
  const [identifier, setIdentifier] = useState('')
  const [step, setStep] = useState<Step>('identifier')
  const [code, setCode] = useState('')
  const [expiresIn, setExpiresIn] = useState(600)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const err = searchParams.get('error')
    if (err === 'magic_link_expired') setError(t('login.magicLinkExpired'))
    else if (err === 'account_not_active') setError(t('login.accountNotActive'))
  }, [searchParams, t])

  async function handleRequestOtp(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const res = await requestOtp({ identifier: identifier.trim(), channel: 'email' })
      setExpiresIn(res.expiresIn)
      setStep('otp')
    } catch (err: unknown) {
      const status = (err as { status?: number }).status
      const body = (err as { body?: { code?: string } }).body
      if (status === 404 || body?.code === 'USER_NOT_FOUND') {
        setError(t('login.userNotFound'))
      } else {
        setError(err instanceof Error ? err.message : t('errors.generic'))
      }
    } finally {
      setLoading(false)
    }
  }

  async function handleVerifyOtp(e: React.FormEvent) {
    e.preventDefault()
    if (code.length < 6) return
    setError(null)
    setLoading(true)
    try {
      const user = await verifyOtp({ identifier: identifier.trim(), code })
      navigate(user.is_admin ? '/admin' : '/dashboard')
    } catch (err: unknown) {
      const status = (err as { status?: number }).status
      if (status === 429) {
        setError(t('errors.tooManyAttempts'))
      } else if (status === 403) {
        setError(t('errors.accountNotActive'))
      } else {
        setError(t('errors.wrongCode'))
      }
      setCode('')
    } finally {
      setLoading(false)
    }
  }

  const handleExpired = useCallback(() => {
    setError(t('errors.expired'))
    setStep('identifier')
    setCode('')
  }, [t])

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-md p-8">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
            className="w-8 h-8 text-accent-400">
            <path fillRule="evenodd"
              d="M14.615 1.595a.75.75 0 0 1 .359.852L12.982 9.75h7.268a.75.75 0 0 1 .548 1.262l-10.5 11.25a.75.75 0 0 1-1.272-.71l1.992-7.302H3.75a.75.75 0 0 1-.548-1.262l10.5-11.25a.75.75 0 0 1 .913-.143Z"
              clipRule="evenodd" />
          </svg>
          <span className="text-2xl font-bold text-brand-700">Kilterinlinna</span>
        </div>

        <h1 className="text-center text-brand-700 font-semibold text-lg mb-6">
          {t('login.title')}
        </h1>

        {step === 'identifier' ? (
          <form onSubmit={handleRequestOtp} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-brand-700 mb-1">
                {t('login.identifier')}
              </label>
              <input
                type="email"
                value={identifier}
                onChange={e => setIdentifier(e.target.value)}
                placeholder={t('login.placeholder')}
                autoComplete="email"
                required
                className="w-full rounded-lg border-brand-100 focus:border-brand-500 focus:ring-brand-500"
              />
            </div>
            {error && <p className="text-red-500 text-sm">{error}</p>}
            <button type="submit" disabled={loading || !identifier}
              className="w-full bg-brand-500 hover:bg-brand-600 text-white font-medium py-2.5 rounded-lg
                disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {loading ? t('login.sending') : t('login.sendCode')}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} className="space-y-5">
            <p className="text-sm text-brand-600 text-center">
              {t('login.otpPrompt')}{' '}
              <strong>{identifier}</strong>.
            </p>
            <p className="text-sm text-center text-brand-500">
              {t('login.codeExpires')} <CountdownTimer seconds={expiresIn} onExpired={handleExpired} />
            </p>
            <OtpInput value={code} onChange={setCode} disabled={loading} />
            {error && <p className="text-red-500 text-sm text-center">{error}</p>}
            <button type="submit" disabled={loading || code.length < 6}
              className="w-full bg-brand-500 hover:bg-brand-600 text-white font-medium py-2.5 rounded-lg
                disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {loading ? t('login.verifying') : t('login.verify')}
            </button>
            <button type="button" onClick={() => { setStep('identifier'); setCode(''); setError(null) }}
              className="w-full text-sm text-brand-500 hover:underline">
              {t('login.changeOrResend')}
            </button>
          </form>
        )}

        <p className="mt-6 text-center text-sm text-brand-500">
          {t('login.noAccount')}{' '}
          <a href="/register" className="text-brand-600 hover:underline font-medium">
            {t('login.register')}
          </a>
        </p>
        <p className="mt-2 text-center">
          <a href="/ohje" className="text-brand-400 hover:underline text-xs">
            {t('login.guide')}
          </a>
        </p>
      </div>
    </div>
  )
}
